import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, RefreshCw, FileText, ExternalLink, Save, Download, Users, Share2, XCircle, MessageCircle, Trash2, ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { t, STATUSES, ADMIN_SET_STATUSES } from "../lib/i18n";
import { api, setAuthToken, loadAuthToken, fileDownloadUrl, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";

const STATUS_BADGE = {
  "Received":            "bg-slate-100 text-slate-800",
  "Documents Checking":  "bg-yellow-100 text-yellow-900",
  "In Process":          "bg-blue-100 text-blue-800",
  "Completed":           "bg-green-100 text-green-800",
  "Rejected":            "bg-red-100 text-red-800",
  "Transferred":         "bg-purple-100 text-purple-800",
  "Assigned":            "bg-indigo-100 text-indigo-800",
};

const AdminDashboardPage = () => {
  const { tr } = useLang();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [token, setToken] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [me, setMe] = useState(null);
  const PAGE_SIZE = 15;

  useEffect(() => {
    const tk = loadAuthToken();
    if (!tk) { navigate("/admin/login"); return; }
    setToken(tk);
    try { setMe(JSON.parse(localStorage.getItem("ap_admin_user") || "null")); } catch {}
    fetchAll(tk);
    // eslint-disable-next-line
  }, []);

  const fetchAll = async (tk) => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filter && filter !== "all") params.status = filter;
      if (q.trim()) params.q = q.trim();
      const [leadsRes, statsRes] = await Promise.all([
        api.get("/admin/leads", { params }),
        api.get("/admin/leads/stats"),
      ]);
      const data = leadsRes.data || {};
      // Backward compat: if API returns list (unlikely now) or object with items
      if (Array.isArray(data)) {
        setLeads(data); setTotalPages(1); setTotal(data.length);
      } else {
        setLeads(data.items || []); setTotalPages(data.pages || 1); setTotal(data.total || 0);
      }
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthToken(null);
        navigate("/admin/login");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    const timer = setTimeout(() => fetchAll(token), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [filter, q, page]);

  // reset to page 1 on filter/search change
  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [filter, q]);

  const logout = () => {
    setAuthToken(null);
    localStorage.removeItem("ap_admin_user");
    navigate("/admin/login");
  };

  const updateStatus = async (leadId, status, adminNote, rejectReason) => {
    setSaving(true);
    try {
      const body = { status, admin_note: adminNote };
      if (status === "Rejected" && rejectReason) body.reject_reason = rejectReason;
      const res = await api.patch(`/admin/leads/${leadId}`, body);
      setLeads((prev) => prev.map((l) => (l.id === leadId ? res.data : l)));
      setSelected(res.data);
      toast.success(tr({ en: "Updated", hi: "अपडेट हुआ" }));
      const statsRes = await api.get("/admin/leads/stats");
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const transferLead = async (leadId, retailerIds, broadcast) => {
    setSaving(true);
    try {
      const res = await api.post(`/admin/leads/${leadId}/transfer`, { retailer_ids: retailerIds || [], broadcast: !!broadcast });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? res.data : l)));
      setSelected(res.data);
      toast.success(tr({ en: "Transferred to partners", hi: "पार्टनर को स्थानांतरित" }));
      const statsRes = await api.get("/admin/leads/stats");
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transfer failed");
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => leads, [leads]);
  const isSuper = me?.role === "super_admin";

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete request ${lead.request_id}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Deleted");
      fetchAll(token);
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-teal-700 font-semibold">{tr(t.nav.admin)}</div>
          <h1 className="mt-1 text-2xl sm:text-3xl font-heading font-semibold text-slate-900">{tr(t.admin.dashboard)}</h1>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> {tr(t.admin.signout)}
        </Button>
      </div>

      {/* Nav + Export bar */}
      <div className="flex flex-wrap justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate("/admin/retailers")} data-testid="admin-nav-retailers-btn">
            <Users className="w-4 h-4 mr-2" /> {tr({ en: "B2B Partners", hi: "B2B पार्टनर" })}
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/users")} data-testid="admin-nav-users-btn">
            <Users className="w-4 h-4 mr-2" /> {tr({ en: "Users", hi: "यूज़र" })}
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/branches")} data-testid="admin-nav-branches-btn">
            <Users className="w-4 h-4 mr-2" /> {tr({ en: "Branches", hi: "शाखाएँ" })}
          </Button>
          <Button variant="outline" onClick={() => navigate("/central")} data-testid="admin-nav-central-btn">
            <Users className="w-4 h-4 mr-2" /> {tr({ en: "Central Queue", hi: "केंद्रीय क्यू" })}
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/services")} data-testid="admin-nav-services-btn">
            <FileText className="w-4 h-4 mr-2" /> {tr({ en: "Services", hi: "सेवाएँ" })}
          </Button>
          {isSuper && <Button variant="outline" onClick={() => navigate("/admin/research")} data-testid="admin-nav-research-btn">रिसर्च और डेली चेक</Button>}
          <Button variant="outline" onClick={() => navigate("/admin/customers")} data-testid="admin-nav-customers-btn">
            <Users className="w-4 h-4 mr-2" /> {tr({ en: "Customer Details", hi: "ग्राहक विवरण" })}
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/accounts")} data-testid="admin-nav-accounts-btn">
            <Users className="w-4 h-4 mr-2" /> हिसाब-किताब
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/store")} data-testid="admin-nav-store-btn">
            <FileText className="w-4 h-4 mr-2" /> {tr({ en: "Digital Store", hi: "डिजिटल स्टोर" })}
          </Button>
          <Button variant="outline" onClick={() => navigate("/admin/settings")} data-testid="admin-nav-settings-btn">
            <Settings className="w-4 h-4 mr-2" /> {tr({ en: "Settings", hi: "सेटिंग्स" })}
          </Button>
        </div>
        <a
          href={`${API}/admin/leads/export?auth=${encodeURIComponent(token || "")}`}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-colors"
          data-testid="admin-export-csv-btn"
        >
          <Download className="w-4 h-4" /> {tr({ en: "Export CSV", hi: "CSV डाउनलोड" })}
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard label={tr({ en: "Total", hi: "कुल" })} value={stats?.total ?? 0} accent="bg-teal-50 text-teal-700" />
        {STATUSES.map((s) => (
          <StatCard key={s} label={tr(t.status[s])} value={stats?.[s] ?? 0} accent={STATUS_BADGE[s]} />
        ))}
      </div>

      {/* Filter bar */}
      <div className="rounded-lg bg-white border border-slate-200 p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchAll(token)}
            placeholder={tr({ en: "Search by name, mobile, Request ID…", hi: "नाम, मोबाइल या अनुरोध आईडी से खोजें…" })}
            data-testid="admin-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[200px]" data-testid="admin-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{tr({ en: "All statuses", hi: "सभी स्थिति" })}</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`filter-${s}`}>{tr(t.status[s])}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => fetchAll(token)} data-testid="admin-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg bg-white border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-leads-table">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium w-10">#</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.reqId)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.name)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.mobile)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.service)}</th>
                <th className="px-4 py-3 font-medium">{tr({ en: "Docs", hi: "दस्तावेज़" })}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.status)}</th>
                <th className="px-4 py-3 font-medium">{tr(t.common.created)}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">{tr({ en: "Loading…", hi: "लोड हो रहा है…" })}</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan="9" className="px-4 py-10 text-center text-slate-500">{tr({ en: "No requests yet.", hi: "अभी कोई आवेदन नहीं।" })}</td></tr>
              )}
              {!loading && filtered.map((l, idx) => (
                <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => setSelected(l)} data-testid={`row-${l.request_id}`}>
                  <td className="px-4 py-3 text-slate-400 text-xs font-mono">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                  <td className="px-4 py-3 font-medium text-slate-900">{l.request_id}{l.need_info?.active && <span className="ml-2 inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" title="Info Needed" />}</td>
                  <td className="px-4 py-3 text-slate-800">{l.name}</td>
                  <td className="px-4 py-3 text-slate-700">{l.mobile}</td>
                  <td className="px-4 py-3 text-slate-700">{l.service_name}{l.custom_work ? ` · ${l.custom_work}` : ""}</td>
                  <td className="px-4 py-3 text-slate-700">{l.files?.length ?? 0}</td>
                  <td className="px-4 py-3"><Badge className={`${STATUS_BADGE[l.status]} rounded-full px-2.5 py-0.5`}>{tr(t.status[l.status])}</Badge></td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button className="text-teal-700 hover:underline text-xs font-medium mr-2" onClick={(e) => { e.stopPropagation(); navigate(`/workspace/${l.id}`); }} data-testid={`workspace-${l.request_id}`}>Workspace</button>
                    <button className="text-teal-700 hover:underline text-xs font-medium mr-2" data-testid={`view-${l.request_id}`}>{tr({ en: "View", hi: "देखें" })}</button>
                    {isSuper && (
                      <button className="text-red-600 hover:underline text-xs font-medium" onClick={(e) => { e.stopPropagation(); deleteLead(l); }} data-testid={`delete-lead-${l.request_id}`}><Trash2 className="w-3 h-3 inline" /> Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-slate-100 text-sm" data-testid="admin-pagination">
            <div className="text-slate-600">
              {tr({ en: "Showing", hi: "दिखा रहे" })} <b>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)}</b> {tr({ en: "of", hi: "में से" })} <b>{total}</b>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, page - 3), Math.max(0, page - 3) + 5).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-md text-sm font-medium ${p === page ? "bg-teal-700 text-white" : "bg-white text-slate-700 hover:bg-slate-100 border border-slate-200"}`}
                  data-testid={`page-${p}`}
                >
                  {p}
                </button>
              ))}
              <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage(page + 1)} data-testid="page-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>

      <LeadDetailDialog
        lead={selected}
        token={token}
        onClose={() => setSelected(null)}
        onSave={updateStatus}
        onTransfer={transferLead}
        saving={saving}
        tr={tr}
      />
    </div>
  );
};

const StatCard = ({ label, value, accent }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest ${accent}`}>{label}</div>
    <div className="mt-2 text-2xl font-heading font-semibold text-slate-900">{value}</div>
  </div>
);

const LeadDetailDialog = ({ lead, token, onClose, onSave, onTransfer, saving, tr }) => {
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [retailers, setRetailers] = useState([]);
  const [pickedRetailer, setPickedRetailer] = useState("");

  useEffect(() => {
    if (lead) {
      setStatus(lead.status);
      setNote(lead.admin_note || "");
      setRejectReason(lead.reject_reason || "");
      setPickedRetailer("");
    }
  }, [lead]);

  useEffect(() => {
    if (lead) {
      api.get("/admin/retailers", { params: { status: "active" } })
        .then((r) => setRetailers(r.data || []))
        .catch(() => {});
    }
  }, [lead]);

  if (!lead) return null;

  const waCustomer = `Namaste ${lead.name}, aapka Ashish Point request ${lead.request_id} (${lead.service_name}) — status: ${lead.status}${lead.reject_reason ? ". Reason: " + lead.reject_reason : ""}`;

  return (
    <Dialog open={!!lead} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{lead.request_id} — {lead.name}</DialogTitle>
          <DialogDescription>{lead.service_name}{lead.custom_work ? ` · ${lead.custom_work}` : ""}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Info label={tr(t.common.mobile)} value={lead.mobile} />
            <Info label={tr(t.common.email)} value={lead.email || "—"} />
            <Info className="col-span-2" label={tr(t.common.address)} value={lead.address} />
            {lead.notes && <Info className="col-span-2" label={tr(t.common.notes)} value={lead.notes} />}
            <Info label={tr(t.common.created)} value={new Date(lead.created_at).toLocaleString()} />
            <Info label={tr(t.common.updated)} value={new Date(lead.updated_at).toLocaleString()} />
            {lead.deadline && <Info label={tr({ en: "Deadline", hi: "समय-सीमा" })} value={lead.deadline} />}
            {lead.assigned_retailer_id && <Info label={tr({ en: "Assigned To", hi: "सौंपा गया" })} value={retailers.find((r) => r.id === lead.assigned_retailer_id)?.shop_name || lead.assigned_retailer_id} />}
          </div>

          {lead.dynamic_fields && Object.keys(lead.dynamic_fields).length > 0 && (
            <div className="rounded-md bg-teal-50 border border-teal-200 p-3">
              <div className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-2">{tr({ en: "Service-specific answers filled by branch/customer", hi: "शाखा/ग्राहक द्वारा भरी अतिरिक्त जानकारी" })}</div>
              <ul className="text-sm text-slate-800 space-y-1">
                {Object.entries(lead.dynamic_fields).map(([k, v]) => (
                  <li key={k} className="flex gap-2"><span className="font-medium min-w-[140px] text-slate-600">{k}:</span> <span className="flex-1">{String(v)}</span></li>
                ))}
              </ul>
            </div>
          )}

          {lead.source === "branch" && lead.source_branch_name && (
            <div className="text-xs text-slate-500"><b>Submitted by branch:</b> {lead.source_branch_name} · <b>Operator:</b> {lead.created_by_name}</div>
          )}

          {lead.reject_reason && (
            <div className="rounded-md bg-red-50 border border-red-100 p-3">
              <div className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-1">{tr({ en: "Reject Reason", hi: "अस्वीकृति कारण" })}</div>
              <div className="text-sm text-slate-800">{lead.reject_reason}</div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr({ en: "Documents", hi: "दस्तावेज़" })}</div>
            {(!lead.files || lead.files.length === 0) && <div className="text-slate-500 text-sm">{tr({ en: "No documents uploaded.", hi: "कोई दस्तावेज़ नहीं।" })}</div>}
            <ul className="space-y-2">
              {(lead.files || []).map((f) => (
                <li key={f.file_id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-teal-700 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate">{f.original_filename}</div>
                      <div className="text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB · {f.content_type}</div>
                    </div>
                  </div>
                  <a
                    href={fileDownloadUrl(f.file_id, token)}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-teal-700 hover:underline text-xs font-medium"
                    data-testid={`download-file-${f.file_id}`}
                  >
                    {tr({ en: "Open", hi: "खोलें" })} <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {(lead.receipt_files && lead.receipt_files.length > 0) && (
            <div>
              <div className="text-xs uppercase tracking-widest text-green-700 font-semibold mb-2">{tr({ en: "Partner Receipts", hi: "पार्टनर रसीद" })}</div>
              <ul className="space-y-2">
                {lead.receipt_files.map((f) => (
                  <li key={f.file_id} className="flex items-center justify-between rounded-md border border-green-200 bg-green-50/40 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-green-700 shrink-0" />
                      <div className="text-sm text-slate-800 truncate">{f.original_filename}</div>
                    </div>
                    <a href={fileDownloadUrl(f.file_id, token)} target="_blank" rel="noopener noreferrer" className="text-green-700 hover:underline text-xs font-medium inline-flex items-center gap-1">
                      {tr({ en: "Open", hi: "खोलें" })} <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr(t.common.status)}</div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADMIN_SET_STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{tr(t.status[s])}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">{tr({ en: "Note for customer", hi: "ग्राहक हेतु नोट" })}</div>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={tr({ en: "Optional message shown on tracking page", hi: "वैकल्पिक संदेश जो ट्रैकिंग पर दिखेगा" })} data-testid="admin-note-input" />
            </div>
          </div>

          {status === "Rejected" && (
            <div>
              <div className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-2">{tr({ en: "Reason for Rejection", hi: "अस्वीकृति का कारण" })}</div>
              <Textarea rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={tr({ en: "Explain briefly why this request is rejected", hi: "संक्षेप में बताएं क्यों अस्वीकृत" })} data-testid="admin-reject-reason" />
            </div>
          )}

          {/* Transfer to B2B partner */}
          <div className="rounded-lg border border-purple-100 bg-purple-50/40 p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-purple-700 font-semibold">{tr({ en: "Transfer to B2B Partner", hi: "B2B पार्टनर को स्थानांतरण" })}</div>
            <p className="text-xs text-slate-600">{tr({ en: "Broadcast to all active partners (first Accept wins) or pick one.", hi: "सभी सक्रिय पार्टनर को भेजें (पहला Accept जीतता है) या एक चुनें।" })}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => onTransfer(lead.id, [], true)} disabled={saving} data-testid="transfer-broadcast-btn">
                <Share2 className="w-4 h-4 mr-2" /> {tr({ en: "Broadcast to All", hi: "सभी को भेजें" })}
              </Button>
              <div className="flex gap-2">
                <Select value={pickedRetailer} onValueChange={setPickedRetailer}>
                  <SelectTrigger data-testid="transfer-retailer-select"><SelectValue placeholder={tr({ en: "Pick partner", hi: "पार्टनर चुनें" })} /></SelectTrigger>
                  <SelectContent>
                    {retailers.map((r) => <SelectItem key={r.id} value={r.id}>{r.shop_name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button disabled={!pickedRetailer || saving} onClick={() => onTransfer(lead.id, [pickedRetailer], false)} data-testid="transfer-pick-btn">
                  Send
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-wrap gap-2">
          <a
            href={`https://wa.me/${lead.mobile.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(waCustomer)}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-green-600 hover:bg-green-700 text-white text-sm font-medium"
            data-testid="admin-wa-customer-btn"
          >
            <MessageCircle className="w-4 h-4" /> {tr({ en: "WhatsApp Customer", hi: "ग्राहक को WhatsApp" })}
          </a>
          <Button variant="outline" onClick={onClose} data-testid="close-dialog-btn">{tr({ en: "Close", hi: "बंद करें" })}</Button>
          <Button
            onClick={() => onSave(lead.id, status, note, rejectReason)}
            disabled={saving || (status === "Rejected" && !rejectReason.trim())}
            className="bg-teal-700 hover:bg-teal-800 text-white"
            data-testid="save-lead-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? tr(t.common.submitting) : tr({ en: "Save changes", hi: "सहेजें" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Info = ({ label, value, className = "" }) => (
  <div className={className}>
    <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</div>
    <div className="mt-1 text-slate-800 whitespace-pre-wrap">{value}</div>
  </div>
);

export default AdminDashboardPage;
